
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blogPosts';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [comment, setComment] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const post = blogPosts.find(p => p.slug === slug);
  const relatedPosts = blogPosts.filter(p => p.slug !== slug).slice(0, 3);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-green-50/30 to-orange-50/20">
        <Header />
        <div className="text-center pt-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-red-400 to-orange-400 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-search-line text-white text-3xl"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">The article you're looking for doesn't exist or has been moved.</p>
            <motion.button 
              onClick={() => navigate('/blog')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-3 rounded-full font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Blog
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setComment({ name: '', email: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sidebarVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94],
        staggerChildren: 0.1
      }
    }
  };

  const relatedCardVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50/30 to-orange-50/20">
      <Header />
      
      {/* Subtle background doodles */}
      <div className="fixed inset-0 pointer-events-none opacity-5 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Simple%20line%20art%20doodles%20of%20fruits%20and%20vegetables%20scattered%20pattern%2C%20minimalist%20hand-drawn%20style%2C%20very%20light%20and%20subtle%2C%20apple%20pear%20banana%20carrot%20tomato%2C%20clean%20white%20background%2C%20delicate%20illustration%20for%20web%20design&width=1920&height=1080&seq=blog-detail-bg-doodles&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Hero Image */}
      <section className="relative h-[70vh] overflow-hidden mt-20">
        <motion.img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="max-w-4xl"
          >
            <motion.div
              className="flex items-center text-white/80 text-sm mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <motion.button 
                onClick={() => navigate('/blog')}
                className="hover:text-white transition-colors duration-300 flex items-center"
                whileHover={{ x: -3 }}
                transition={{ duration: 0.2 }}
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Blog
              </motion.button>
              <span className="mx-4">•</span>
              <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">{post.category}</span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {post.title}
            </motion.h1>
            
            <motion.div 
              className="flex items-center text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-orange-400 rounded-2xl flex items-center justify-center mr-4">
                <i className="ri-user-line text-white"></i>
              </div>
              <div>
                <p className="font-semibold text-lg">{post.author}</p>
                <div className="flex items-center text-sm text-white/70">
                  <span>{post.date}</span>
                  <span className="mx-2">•</span>
                  <span>{post.readTime}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-12 md:p-16 border border-white/20"
          >
            {/* Article Body */}
            <div className="prose prose-lg max-w-none">
              <motion.p 
                className="text-2xl text-gray-600 mb-10 leading-relaxed font-light"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {post.excerpt}
              </motion.p>

              <motion.h2 
                className="text-3xl font-bold text-gray-800 mb-6 mt-12"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Understanding the Export Market
              </motion.h2>
              <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                The global export market for fresh produce has experienced unprecedented growth in recent years. 
                With increasing demand for healthy, organic fruits and vegetables worldwide, exporters are finding 
                new opportunities to expand their reach and establish profitable partnerships across continents.
              </p>

              <motion.div 
                className="bg-gradient-to-r from-green-50 to-orange-50 border-l-4 border-green-500 p-8 my-12 rounded-r-2xl shadow-sm"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-orange-400 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-lightbulb-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium text-lg leading-relaxed">
                      <strong>Pro Tip:</strong> Quality certification is crucial for success in international markets. 
                      Ensure your products meet the specific standards required by your target countries.
                    </p>
                  </div>
                </div>
              </motion.div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6 mt-12">Key Success Factors</h2>
              <motion.ul 
                className="space-y-5 mb-10"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, staggerChildren: 0.1 }}
              >
                {[
                  'Maintaining consistent quality standards throughout the supply chain',
                  'Building strong relationships with international distributors',
                  'Understanding and complying with local regulations and preferences',
                  'Implementing sustainable and environmentally friendly practices'
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-lg leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>

              <motion.div 
                className="my-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20fruit%20and%20vegetable%20processing%20facility%20with%20workers%20in%20white%20coats%20sorting%20fresh%20organic%20produce%20on%20conveyor%20belts%2C%20industrial%20food%20processing%20plant%2C%20quality%20control%2C%20clean%20environment%2C%20professional%20lighting%2C%20export%20business%20operations&width=800&height=400&seq=blog-detail-facility-modern&orientation=landscape"
                  alt="Processing Facility"
                  className="w-full rounded-2xl shadow-lg"
                />
                <p className="text-center text-gray-500 text-base mt-4 font-medium">
                  Modern processing facilities ensure quality and safety standards
                </p>
              </motion.div>

              <h2 className="text-3xl font-bold text-gray-800 mb-6 mt-12">Market Trends and Opportunities</h2>
              <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                Current market trends show a significant shift towards organic and sustainably produced fruits and vegetables. 
                Consumers are increasingly conscious about the origin of their food and the environmental impact of their 
                purchasing decisions. This presents excellent opportunities for exporters who can demonstrate their commitment 
                to sustainable practices and organic certification.
              </p>

              <motion.blockquote 
                className="border-l-4 border-orange-400 bg-gradient-to-r from-orange-50 to-yellow-50 p-8 my-12 rounded-r-2xl italic text-xl text-gray-700 font-medium leading-relaxed"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                "Technology plays a crucial role in modern export operations. From blockchain-based traceability systems 
                to AI-powered quality control, innovative solutions are helping exporters maintain competitive advantages."
              </motion.blockquote>

              <p className="text-gray-700 mb-8 leading-relaxed text-lg">
                As we move forward, the export industry will continue to evolve with changing consumer preferences, 
                technological advancements, and global market dynamics. Success will depend on adaptability, innovation, 
                and a commitment to excellence in every aspect of the operation.
              </p>
            </div>

            {/* Share Section */}
            <motion.div 
              className="border-t border-gray-200/50 pt-10 mt-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-6">Share this article</h3>
              <div className="flex space-x-4">
                {[
                  { icon: 'ri-facebook-fill', color: 'hover:bg-blue-500', label: 'Facebook' },
                  { icon: 'ri-twitter-fill', color: 'hover:bg-blue-400', label: 'Twitter' },
                  { icon: 'ri-linkedin-fill', color: 'hover:bg-blue-600', label: 'LinkedIn' },
                  { icon: 'ri-mail-fill', color: 'hover:bg-gray-500', label: 'Email' }
                ].map((social) => (
                  <motion.button
                    key={social.label}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className={`w-12 h-12 bg-gray-100/80 hover:text-white rounded-2xl flex items-center justify-center text-gray-600 ${social.color} transition-all duration-300 shadow-sm`}
                  >
                    <i className={social.icon}></i>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.article>
        </div>
      </section>

      {/* Related Posts */}
      <section className="py-20 px-6 bg-white/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl font-bold text-gray-800 text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Related Articles
          </motion.h2>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={sidebarVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {relatedPosts.map((relatedPost) => (
              <motion.article
                key={relatedPost.id}
                variants={relatedCardVariants}
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                  boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.1)"
                }}
                whileTap={{ scale: 0.98 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 cursor-pointer group border border-white/20"
                onClick={() => navigate(`/blog/${relatedPost.slug}`)}
              >
                <div className="relative overflow-hidden h-48">
                  <motion.img
                    src={relatedPost.image}
                    alt={relatedPost.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-white/90 backdrop-blur-sm text-green-600 px-2 py-1 rounded-full text-xs font-semibold">
                      {relatedPost.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="text-sm text-gray-500 mb-3 flex items-center">
                    <i className="ri-calendar-line mr-1 text-green-500"></i>
                    {relatedPost.date}
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-green-600 transition-colors duration-300">
                    {relatedPost.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                    {relatedPost.excerpt}
                  </p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Comment Form */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-10 border border-white/20"
          >
            <div className="text-center mb-10">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="w-16 h-16 bg-gradient-to-br from-green-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-4"
              >
                <i className="ri-chat-3-line text-white text-2xl"></i>
              </motion.div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Join the Conversation</h3>
              <p className="text-gray-600">Share your thoughts and insights on this article</p>
            </div>
            
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-2xl mb-8 flex items-center"
              >
                <i className="ri-check-circle-line mr-3 text-xl"></i>
                <span className="font-medium">Thank you for your comment! We appreciate your feedback.</span>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-8 flex items-center"
              >
                <i className="ri-error-warning-line mr-3 text-xl"></i>
                <span className="font-medium">There was an error submitting your comment. Please try again.</span>
              </motion.div>
            )}

            <form onSubmit={handleCommentSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-3">
                    Full Name *
                  </label>
                  <motion.input
                    whileFocus={{ 
                      scale: 1.02, 
                      boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.1)" 
                    }}
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={comment.name}
                    onChange={(e) => setComment({ ...comment, name: e.target.value })}
                    className="w-full px-6 py-4 border border-gray-200/50 rounded-2xl focus:ring-2 focus:ring-green-400/30 focus:border-green-400/50 focus:outline-none transition-all duration-300 bg-white/50 backdrop-blur-sm text-gray-700 placeholder-gray-400"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3">
                    Email Address *
                  </label>
                  <motion.input
                    whileFocus={{ 
                      scale: 1.02, 
                      boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.1)" 
                    }}
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={comment.email}
                    onChange={(e) => setComment({ ...comment, email: e.target.value })}
                    className="w-full px-6 py-4 border border-gray-200/50 rounded-2xl focus:ring-2 focus:ring-green-400/30 focus:border-green-400/50 focus:outline-none transition-all duration-300 bg-white/50 backdrop-blur-sm text-gray-700 placeholder-gray-400"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-3">
                  Your Comment *
                </label>
                <motion.textarea
                  whileFocus={{ 
                    scale: 1.02, 
                    boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.1)" 
                  }}
                  id="message"
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  value={comment.message}
                  onChange={(e) => setComment({ ...comment, message: e.target.value })}
                  className="w-full px-6 py-4 border border-gray-200/50 rounded-2xl focus:ring-2 focus:ring-green-400/30 focus:border-green-400/50 focus:outline-none transition-all duration-300 bg-white/50 backdrop-blur-sm text-gray-700 placeholder-gray-400 resize-none"
                  placeholder="Share your thoughts, insights, or questions about this article..."
                />
                <div className="text-right text-sm text-gray-500 mt-2">
                  <span className={comment.message.length > 450 ? 'text-orange-500' : comment.message.length > 500 ? 'text-red-500' : ''}>
                    {comment.message.length}/500 characters
                  </span>
                </div>
              </div>
              
              <motion.button
                type="submit"
                disabled={isSubmitting || comment.message.length > 500}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-8 rounded-2xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg disabled:hover:scale-100 whitespace-nowrap"
              >
                {isSubmitting ? (
                  <motion.span 
                    className="flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <motion.i 
                      className="ri-loader-4-line mr-3 text-xl"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Publishing Comment...
                  </motion.span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-send-plane-line mr-3"></i>
                    Publish Comment
                  </span>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
